// src/components/VPSControl.jsx
// VPS Management Component - CRUD operations, status monitoring, bulk assignment
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';
import ConfirmDialog from './ConfirmDialog';

function VPSControl() {
  const [vpsList, setVpsList] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingVPS, setEditingVPS] = useState(null);

  const [filters, setFilters] = useState({ status: '', campaign_id: '' });

  const [formData, setFormData] = useState({
    name: '',
    ip_address: '',
    proxy_name: '',
    proxy_port: '',
    proxy_user: '',
    proxy_password: '',
    campaign_id: '',
    click_only: false,
    status: 'active'
  });

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, name: '' });

  // ── Bulk selection state ──────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCampaignId, setBulkCampaignId] = useState('__nochange__');
  const [bulkClickOnly, setBulkClickOnly] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkFeedback, setBulkFeedback] = useState('');
  const [rangeInput, setRangeInput] = useState('');
  const rangeRef = useRef(null);

  useLockBodyScroll(showModal);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchVPSList = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.campaign_id) params.campaign_id = filters.campaign_id;
      const response = await axios.get('/vps', { params });
      setVpsList(response.data.vps);
    } catch (error) {
      console.error('Failed to fetch VPS list:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get('/campaigns');
      setCampaigns([...response.data.campaigns].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/vps/stats/summary');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch VPS stats:', error);
    }
  };

  useEffect(() => { fetchCampaigns(); fetchStats(); }, []);
  useEffect(() => { fetchVPSList(); }, [fetchVPSList]);

  // ── Checkbox selection ────────────────────────────────────────────────────
  const allVisibleIds = vpsList.map((v) => v.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));
  const someSelected = allVisibleIds.some((id) => selectedIds.has(id)) && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allVisibleIds));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Range selector ────────────────────────────────────────────────────────
  // Parses "1-10" or "1,3,6" into row positions (1-based) and selects those VPS.
  const applyRange = () => {
    const input = rangeInput.trim();
    if (!input) return;

    const positions = new Set();

    for (const part of input.split(',')) {
      const trimmed = part.trim();
      const rangeMatch = trimmed.match(/^(\d+)\s*[-–]\s*(\d+)$/);
      if (rangeMatch) {
        const from = parseInt(rangeMatch[1], 10);
        const to = parseInt(rangeMatch[2], 10);
        for (let i = Math.min(from, to); i <= Math.max(from, to); i++) positions.add(i);
      } else if (/^\d+$/.test(trimmed)) {
        positions.add(parseInt(trimmed, 10));
      }
    }

    const newSelected = new Set(selectedIds);
    positions.forEach((pos) => {
      const vps = vpsList[pos - 1]; // 1-based
      if (vps) newSelected.add(vps.id);
    });
    setSelectedIds(newSelected);
    setRangeInput('');
  };

  // ── Bulk assign ───────────────────────────────────────────────────────────
  const handleBulkAssign = async () => {
    if (selectedIds.size === 0) return;
    if (bulkCampaignId === '__nochange__' && bulkClickOnly === '') return;
    setBulkLoading(true);
    setBulkFeedback('');
    try {
      const payload = { vps_ids: [...selectedIds] };
      if (bulkCampaignId !== '__nochange__') payload.campaign_id = bulkCampaignId || null;
      if (bulkClickOnly !== '') payload.click_only = bulkClickOnly === 'true';

      const res = await axios.put('/vps/bulk-assign', payload);

      const parts = [];
      if (bulkCampaignId !== '__nochange__') {
        const campaignName = bulkCampaignId
          ? campaigns.find((c) => c.id === bulkCampaignId)?.name || 'campaign'
          : null;
        parts.push(campaignName ? `assigned to "${campaignName}"` : 'unassigned');
      }
      if (bulkClickOnly !== '') {
        parts.push(bulkClickOnly === 'true' ? 'set to Click Only' : 'set to Full Auto');
      }
      setBulkFeedback(`${res.data.updated} VPS ${parts.join(' & ')}`);
      setSelectedIds(new Set());
      setBulkCampaignId('__nochange__');
      setBulkClickOnly('');
      fetchVPSList();
      fetchStats();
    } catch (error) {
      setBulkFeedback(error.response?.data?.error || 'Bulk assign failed');
    } finally {
      setBulkLoading(false);
      setTimeout(() => setBulkFeedback(''), 4000);
    }
  };

  // ── Form handling ─────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) {
      newErrors.name = 'Name is required';
    } else if (!/^[a-zA-Z0-9_-]{1,100}$/.test(formData.name)) {
      newErrors.name = 'Name must be alphanumeric (hyphens/underscores allowed)';
    }
    if (!formData.ip_address) {
      newErrors.ip_address = 'IP address is required';
    } else if (!/^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(formData.ip_address)) {
      newErrors.ip_address = 'Invalid IPv4 address format';
    }
    if (formData.proxy_port) {
      const port = parseInt(formData.proxy_port, 10);
      if (isNaN(port) || port < 1 || port > 65535) newErrors.proxy_port = 'Port must be 1–65535';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const data = {
        ...formData,
        proxy_port: formData.proxy_port ? parseInt(formData.proxy_port, 10) : null,
        campaign_id: formData.campaign_id || null,
      };
      if (editingVPS) {
        await axios.put(`/vps/${editingVPS.id}`, data);
      } else {
        await axios.post('/vps', data);
      }
      fetchVPSList();
      fetchStats();
      setShowModal(false);
      resetForm();
    } catch (error) {
      setFormError(error.response?.data?.error || 'Failed to save VPS');
    }
  };

  const handleEdit = (vps) => {
    setEditingVPS(vps);
    setFormError('');
    setFormData({
      name: vps.name,
      ip_address: vps.ip_address,
      proxy_name: vps.proxy_name || '',
      proxy_port: vps.proxy_port || '',
      proxy_user: vps.proxy_user || '',
      proxy_password: vps.proxy_password || '',
      campaign_id: vps.campaign_id || '',
      click_only: vps.click_only || false,
      status: vps.status,
    });
    setErrors({});
    setShowModal(true);
  };

  const handleDelete = (id, name) => setConfirmDialog({ open: true, id, name });
  const performDelete = async () => {
    try {
      await axios.delete(`/vps/${confirmDialog.id}`);
      fetchVPSList();
      fetchStats();
    } catch { setCopyFeedback('Failed to delete VPS'); setTimeout(() => setCopyFeedback(''), 2000); }
    setConfirmDialog({ open: false, id: null, name: '' });
  };

  const handleStatusToggle = async (vps) => {
    const newStatus = vps.status === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`/vps/${vps.id}`, { status: newStatus });
      fetchVPSList();
      fetchStats();
    } catch (error) {
      setCopyFeedback('Failed to update VPS status');
      setTimeout(() => setCopyFeedback(''), 2000);
    }
  };

  const resetForm = () => {
    setEditingVPS(null);
    setFormData({
      name: '',
      ip_address: '',
      proxy_name: '',
      proxy_port: '',
      proxy_user: '',
      proxy_password: '',
      campaign_id: '',
      click_only: false,
      status: 'active',
    });
    setErrors({});
  };

  const handleCopy = async (text) => {
    let success = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        success = true;
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        success = document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (err) {
      success = false;
    }
    setCopyFeedback(success ? 'Copied!' : 'Failed to copy');
    setTimeout(() => setCopyFeedback(''), 2000);
  };

  const getStatusColor = (status) =>
    status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';

  const getOnlineStatus = (lastOnline) => {
    if (!lastOnline) return { status: 'Never', color: 'text-gray-400' };
    const diff = (new Date() - new Date(lastOnline)) / 60000;
    if (diff <= 5)    return { status: 'Online',              color: 'text-green-600' };
    if (diff <= 30)   return { status: `${Math.round(diff)}m ago`,        color: 'text-yellow-600' };
    if (diff <= 1440) return { status: `${Math.round(diff / 60)}h ago`,   color: 'text-orange-600' };
    return              { status: `${Math.round(diff / 1440)}d ago`, color: 'text-red-600' };
  };

  const formatDateTime = (d) => d ? new Date(d).toLocaleString() : 'Never';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">VPS Control</h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { fetchVPSList(); fetchStats(); }}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100 shadow-sm disabled:opacity-50"
            title="Refresh data"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Add VPS</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {[
            { label: 'Total VPS',    value: stats.total_vps,    color: 'text-gray-900' },
            { label: 'Active',       value: stats.active_vps,   color: 'text-green-600' },
            { label: 'Paused',       value: stats.paused_vps,   color: 'text-yellow-600' },
            { label: 'Online (5min)',value: stats.online_vps,   color: 'text-blue-600' },
            { label: 'Assigned',     value: stats.assigned_vps, color: 'text-purple-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-500">{label}</div>
              <div className={`text-2xl font-bold ${color}`}>{value || 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters + Range Selector */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-3">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
            <select
              value={filters.campaign_id}
              onChange={(e) => setFilters({ ...filters, campaign_id: e.target.value })}
              className="block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">All Campaigns</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select rows
              <span className="ml-1 font-normal text-gray-400">(e.g. 1-10 or 1,3,6)</span>
            </label>
            <div className="flex gap-2">
              <input
                ref={rangeRef}
                type="text"
                value={rangeInput}
                onChange={(e) => setRangeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyRange()}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 font-mono"
                placeholder="1-10"
              />
              <button
                onClick={applyRange}
                className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm hover:bg-gray-200 whitespace-nowrap"
              >
                Select
              </button>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => setFilters({ status: '', campaign_id: '' })}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </button>
            {selectedIds.size > 0 && (
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-4 py-2 text-sm text-red-500 hover:text-red-700"
              >
                Clear Selection ({selectedIds.size})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* VPS Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : vpsList.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No VPS records found. Click "Add VPS" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {/* Select-all checkbox */}
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proxy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Online</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vpsList.map((vps, idx) => {
                  const onlineStatus = getOnlineStatus(vps.last_online);
                  const isSelected = selectedIds.has(vps.id);
                  return (
                    <tr
                      key={vps.id}
                      className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleOne(vps.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-400 tabular-nums">{idx + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{vps.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-900 font-mono">{vps.ip_address}</span>
                          <button onClick={() => handleCopy(vps.ip_address)} title="Copy" className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vps.campaign_name || <span className="text-gray-400">Not assigned</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vps.proxy_name && vps.proxy_port
                          ? <span className="font-mono">{vps.proxy_name}:{vps.proxy_port}</span>
                          : <span className="text-gray-400">None</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${vps.click_only ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>
                          {vps.click_only ? 'Click Only' : 'Full Auto'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleStatusToggle(vps)}
                          title={vps.status === 'active' ? 'Click to pause' : 'Click to activate'}
                          className={`px-2 py-1 text-xs font-semibold rounded cursor-pointer ${getStatusColor(vps.status)}`}
                        >
                          {vps.status}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${onlineStatus.color}`}>{onlineStatus.status}</div>
                        {vps.last_online && <div className="text-xs text-gray-400">{formatDateTime(vps.last_online)}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        <button onClick={() => handleEdit(vps)} className="text-indigo-600 hover:text-indigo-900">Edit</button>
                        <button onClick={() => handleDelete(vps.id, vps.name)} className="text-red-600 hover:text-red-900">Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk Action Bar — fixed at bottom, visible when rows are selected */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <div className="max-w-7xl mx-auto space-y-2">
            {/* Row 1: count + clear */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">
                {selectedIds.size} VPS selected
              </span>
              <button
                onClick={() => { setSelectedIds(new Set()); setBulkCampaignId('__nochange__'); setBulkClickOnly(''); }}
                className="text-sm text-red-500 hover:text-red-700"
              >
                Clear selection
              </button>
            </div>
            {/* Row 2: assign + mode controls */}
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={bulkCampaignId}
                onChange={(e) => setBulkCampaignId(e.target.value)}
                className="block border border-gray-300 rounded-md px-3 py-2 text-base min-w-0 flex-1 min-h-[44px]"
              >
                <option value="__nochange__">— Campaign: no change —</option>
                <option value="">— Unassign campaign —</option>
                {campaigns.filter((c) => c.status === 'active').map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <select
                value={bulkClickOnly}
                onChange={(e) => setBulkClickOnly(e.target.value)}
                className="block border border-gray-300 rounded-md px-3 py-2 text-base min-h-[44px]"
              >
                <option value="">— Mode: no change —</option>
                <option value="true">Click Only</option>
                <option value="false">Full Auto</option>
              </select>
              <button
                onClick={handleBulkAssign}
                disabled={bulkLoading || (bulkCampaignId === '__nochange__' && bulkClickOnly === '')}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap min-h-[44px]"
              >
                {bulkLoading && (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                Apply
              </button>
            </div>
            {bulkFeedback && (
              <span className={`text-sm font-medium ${bulkFeedback.includes('failed') || bulkFeedback.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
                {bulkFeedback}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 pt-[env(safe-area-inset-top)]"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90dvh] overflow-y-auto overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold mb-6">{editingVPS ? 'Edit VPS' : 'Add New VPS'}</h2>
            {formError && <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800">{formError}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">VPS Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="vps01"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">IP Address <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.ip_address}
                    onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 ${errors.ip_address ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="192.168.1.1"
                  />
                  {errors.ip_address && <p className="mt-1 text-sm text-red-500">{errors.ip_address}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Assigned Campaign</label>
                <select
                  value={formData.campaign_id}
                  onChange={(e) => setFormData({ ...formData, campaign_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">-- Not Assigned --</option>
                  {campaigns.filter((c) => c.status === 'active').map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Proxy Settings</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Proxy Host</label>
                    <input type="text" value={formData.proxy_name} onChange={(e) => setFormData({ ...formData, proxy_name: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="proxy.example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Proxy Port</label>
                    <input type="number" value={formData.proxy_port} onChange={(e) => setFormData({ ...formData, proxy_port: e.target.value })} className={`mt-1 block w-full border rounded-md px-3 py-2 ${errors.proxy_port ? 'border-red-500' : 'border-gray-300'}`} placeholder="8080" min="1" max="65535" />
                    {errors.proxy_port && <p className="mt-1 text-sm text-red-500">{errors.proxy_port}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Proxy Username</label>
                    <input type="text" value={formData.proxy_user} onChange={(e) => setFormData({ ...formData, proxy_user: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="proxyuser" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Proxy Password</label>
                    <input type="password" value={formData.proxy_password} onChange={(e) => setFormData({ ...formData, proxy_password: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="••••••••" />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Automation Settings</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" checked={formData.click_only} onChange={(e) => setFormData({ ...formData, click_only: e.target.checked })} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                      <span className="text-sm font-medium text-gray-700">Click Only Mode</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">When enabled, VPS only generates clicks without full automation</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 -mx-8 -mb-8 px-8 py-4 bg-white border-t flex justify-end gap-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 active:bg-gray-100">Cancel</button>
                <button type="submit" className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-800">{editingVPS ? 'Update VPS' : 'Create VPS'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Copy Feedback Toast */}
      {copyFeedback && (
        <div className="fixed bottom-20 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg z-50">
          {copyFeedback}
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={`Delete VPS "${confirmDialog.name}"?`}
        message="This cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null, name: '' })}
      />
    </div>
  );
}

export default VPSControl;
